import React, { useEffect, useMemo, useRef, useState } from "react"
import { FiMinus, FiSend, FiX, FiZap } from "react-icons/fi"

import awsExports from "../aws-exports"
import { getAppBaseUrl } from "../utils/domain"

const marketOnlyPattern =
  /\b(price|current|market|quote|weekly|week|move|movement|pips?|eurusd|gbpusd|audusd|usdjpy|xauusd|gold|btc|bitcoin|eth|ethereum)\b/i
const helpPattern =
  /\b(help|faqs?|questions?|support|competitions?|comps?|leaderboards?|rank|score|scores?|rules?|join|pricing|plans?|memberships?|pro|premium|partner|accounts?|account setup|connect|connection|mt4|mt5|brokers?|investor|passwords?|traderpass|trader pass|discord|telegram|certificates?|payouts?|verify|verified|trades?|trade history|register|registration|login|log in|sign in|signup|sign up)\b/i
const publicSupportIntentPattern =
  /\b(traderflow|competitions?|comps?|leaderboards?|rank|score|scores?|rules?|join|pricing|plans?|memberships?|account setup|set up|setup|connect|connection|mt4|mt5|brokers?|investor|read-only|traderpass|trader pass|discord|telegram|certificates?|verified trades?|register|registration|login|log in|sign in|signup|sign up|dashboard|website|platform)\b/i
const privateDataPattern =
  /\b(billing|invoice|subscription|payment|password|balance|equity|positions?|private profile|profile data|personal plan|payout status)\b/i
const liveCompetitionPattern =
  /\b(live|current|ongoing|running|active|available|open|now)\b.*\b(competition|competitions|comp|comps|leaderboard)\b|\b(competition|competitions|comp|comps|leaderboard)\b.*\b(live|current|ongoing|running|active|available|open|now)\b/i
const greetingPattern =
  /^(hi|hello|hey|yo|good\s+(morning|afternoon|evening)|help|what can you help with)\b[!.?\s]*$/i
const privateAccountPattern =
  /\b(my|mine|me|i)\b.*\b(account|profile|plan|membership|billing|invoice|subscription|payment|password|balance|equity|trades?|positions?|certificate|payout)\b|\b(account|profile|plan|membership|billing|invoice|subscription|payment|password|balance|equity|trades?|positions?|certificate|payout)\b.*\b(my|mine|me)\b/i
const tradingAdvicePattern =
  /\b(signal|signals|buy|sell|long|short|entry|exit|stop loss|take profit|tp|sl|forecast|prediction|predict|strategy|risk|lot size|position size|trade recommendation|should i trade|market outlook|next move|trend)\b/i
const publicRateLimit = {
  key: "traderflow-help-public-rate",
  maxRequests: 5,
  windowMs: 10 * 60 * 1000,
}
const supportTopicOptions = [
  { label: "Competitions", prompt: "How do Traderflow competitions work?" },
  { label: "Pricing", prompt: "What are Traderflow pricing and plan options?" },
  { label: "Login", prompt: "How do I login or register?" },
  { label: "Account setup", prompt: "How do I set up my MT4 or MT5 account?" },
  { label: "TraderPass", prompt: "What is TraderPass and how do I use it?" },
  { label: "Certificates", prompt: "How do Traderflow certificates work?" },
  { label: "Verified trades", prompt: "How are verified trades shared?" },
  {
    label: "Discord/Telegram",
    prompt: "How do I share to Discord or Telegram?",
  },
]

const knownSymbols = [
  "EURUSD",
  "GBPUSD",
  "AUDUSD",
  "NZDUSD",
  "USDJPY",
  "USDCAD",
  "USDCHF",
  "XAUUSD",
  "BTCUSD",
  "ETHUSD",
]

export default function ForexCopilotChatWidget() {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState("")
  const [messages, setMessages] = useState([])
  const [isSending, setIsSending] = useState(false)
  const messagesEndRef = useRef(null)
  const dashboardUrl = useMemo(() => getDashboardUrl(), [])
  const helpApiUrls = useMemo(() => getHelpApiUrls(), [])
  const visibleMessages = [
    {
      id: "welcome",
      role: "assistant",
      text: "**Traderflow Help:** choose a topic or ask about competitions, pricing, account setup, TraderPass, certificates, Discord/Telegram, or login/register.",
    },
    ...messages,
  ]

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: "end" })
  }, [messages, open])

  async function submitPrompt(prompt) {
    const trimmed = prompt.trim()
    if (!trimmed || isSending) return

    setInput("")
    const userMessage = {
      id: createMessageId(),
      role: "user",
      text: trimmed,
    }
    const previousMessages = messages.slice(-8)
    setMessages(current => [...current, userMessage])
    setIsSending(true)

    const reply = await getTraderflowHelpReply({
      prompt: trimmed,
      helpApiUrls,
      previousMessages,
    })

    setMessages(current => [
      ...current,
      {
        id: createMessageId(),
        role: "assistant",
        text: reply,
      },
    ])
    setIsSending(false)
  }

  async function handleSubmit(event) {
    event.preventDefault()
    await submitPrompt(input)
  }

  return (
    <div className="fixed bottom-5 right-4 z-[70] flex flex-col items-end gap-3 sm:right-6">
      {open && (
        <section className="border-white/15 w-[min(calc(100vw-2rem),380px)] overflow-hidden rounded-lg border bg-black/95 text-white shadow-2xl backdrop-blur">
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="size-9 grid shrink-0 place-items-center rounded-full bg-[#f5b160] text-black">
                <FiZap className="size-5" />
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-sm font-semibold">
                  Traderflow Help
                </h2>
                <p className="truncate text-xs text-white/60">
                  FAQ and competition help
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <span className="border-white/15 rounded-full border px-2 py-1 text-xs text-white/70">
                Limited
              </span>
              <button
                type="button"
                className="size-8 grid place-items-center rounded-md text-white/70 transition hover:bg-white/10 hover:text-white"
                aria-label="Minimize Traderflow Help"
                onClick={() => setOpen(false)}
              >
                <FiMinus className="size-4" />
              </button>
              <button
                type="button"
                className="size-8 grid place-items-center rounded-md text-white/70 transition hover:bg-white/10 hover:text-white"
                aria-label="Close Traderflow Help"
                onClick={() => {
                  setOpen(false)
                  setInput("")
                }}
              >
                <FiX className="size-4" />
              </button>
            </div>
          </div>

          <div className="flex max-h-[420px] min-h-[280px] flex-col justify-end overflow-y-auto px-4 py-4">
            <div className="space-y-3">
              {visibleMessages.map(message => (
                <div
                  key={message.id}
                  className={
                    message.role === "user"
                      ? "ml-auto max-w-[85%] rounded-lg bg-[#f5b160] px-3 py-2 text-sm text-black"
                      : "mr-auto max-w-[88%] whitespace-pre-line rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/90"
                  }
                >
                  {renderFormattedText(message.text)}
                </div>
              ))}
              {isSending && (
                <div className="mr-auto max-w-[88%] rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/60">
                  Checking Traderflow help...
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <form
            className="border-t border-white/10 p-3"
            onSubmit={handleSubmit}
          >
            <div className="mb-3 flex flex-wrap gap-2">
              {supportTopicOptions.map(topic => (
                <button
                  key={topic.label}
                  type="button"
                  className="border-white/15 rounded-full border px-2.5 py-1 text-xs font-medium text-white/75 transition hover:border-[#f5b160] hover:text-[#f5c96d] disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => submitPrompt(topic.prompt)}
                  disabled={isSending}
                >
                  {topic.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                value={input}
                onChange={event => setInput(event.target.value)}
                placeholder="Ask about comps, pricing, accounts, or help"
                className="border-white/15 h-10 min-w-0 flex-1 rounded-md border bg-white/5 px-3 text-sm text-white outline-none transition placeholder:text-white/40 focus:border-[#f5b160]"
                disabled={isSending}
              />
              <button
                type="submit"
                className="size-10 grid shrink-0 place-items-center rounded-md bg-[#f5b160] text-black transition hover:bg-[#f5c96d] disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!input.trim() || isSending}
                aria-label="Send Traderflow Help message"
              >
                <FiSend className="size-4" />
              </button>
            </div>
            <a
              href={dashboardUrl}
              className="mt-3 block text-center text-xs font-medium text-[#f5c96d] transition hover:text-white"
            >
              Log in for Pro Copilot and dashboard tools
            </a>
          </form>
        </section>
      )}

      <button
        type="button"
        className="chatbot-hop relative size-16 group flex items-center justify-center rounded-full p-[2px] bg-white/5 border border-white/10 hover:border-[#f5c96d]/40 shadow-2xl hover:scale-105 active:scale-95 transition-all duration-300"
        aria-label={open ? "Hide Traderflow Help" : "Open Traderflow Help"}
        title="Traderflow Help"
        onClick={() => setOpen(current => !current)}
      >
        {/* Glow ambient background ring */}
        <div className="absolute inset-0 rounded-full bg-[#f5c96d]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-md" />
        
        {/* Inner container */}
        <div className="relative w-full h-full rounded-full flex items-center justify-center bg-[#090514] border border-white/5 shadow-[inset_0_1px_1px_rgba(255,255,255,0.12)]">
          {open ? (
            <FiX className="size-6 text-[#f5c96d]" />
          ) : (
            <div className="relative size-7 flex items-center justify-center">
              {/* Outer pulsing ring inside the orb */}
              <div className="absolute inset-0 rounded-full border border-[#f5c96d]/20 animate-ping opacity-60" style={{ animationDuration: '3s' }} />
              
              {/* Premium custom SVG chatbot icon */}
              <svg 
                viewBox="0 0 24 24" 
                fill="none" 
                className="size-6 text-[#f5c96d] transition-all duration-300 group-hover:scale-110"
                stroke="currentColor" 
                strokeWidth="1.75" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M12 2v2M12 4a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" fill="currentColor" />
                <rect x="4" y="7" width="16" height="12" rx="3" />
                <circle cx="9" cy="12" r="1.25" fill="currentColor" />
                <circle cx="15" cy="12" r="1.25" fill="currentColor" />
                <path d="M9 16h6" />
                <path d="M2 11v4M22 11v4" />
              </svg>
            </div>
          )}
        </div>
      </button>
    </div>
  )
}

async function getTraderflowHelpReply({
  prompt,
  helpApiUrls,
  previousMessages,
}) {
  const publicGateReply = getPublicWebsiteGateReply(prompt)
  if (publicGateReply) {
    return publicGateReply
  }

  if (liveCompetitionPattern.test(prompt)) {
    return fetchLiveCompetitionReply()
  }

  if (helpPattern.test(prompt)) {
    return buildTraderflowHelpReply(prompt)
  }

  if (!helpApiUrls.length) {
    return buildLimitedReply(prompt)
  }

  if (!consumePublicHelpRequest()) {
    return "**Rate limit reached:** log in to the dashboard for account tools, Pro Copilot, and more support."
  }

  for (const helpApiUrl of helpApiUrls) {
    try {
      const response = await fetch(helpApiUrl, {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({
          userMessage: prompt,
          messages: previousMessages,
          accessTier: "public",
          isLoggedIn: false,
          surface: "website-widget",
        }),
      })

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null)
        const errorText = String(errorPayload?.text || "").trim()
        if (errorText) return errorText
        throw new Error(`Traderflow Help API returned ${response.status}`)
      }

      const payload = await response.json()
      const text = String(payload.text || "").trim()
      if (text) return text
    } catch (error) {
      console.warn("[TraderflowHelp] Help API request failed", {
        helpApiUrl,
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  return buildLimitedReply(prompt)
}

function getPublicWebsiteGateReply(prompt) {
  if (greetingPattern.test(prompt)) {
    return "**Traderflow Help:** choose a topic or ask about competitions, pricing, account setup, TraderPass, certificates, Discord/Telegram, or login/register."
  }

  if (
    privateAccountPattern.test(prompt) &&
    (privateDataPattern.test(prompt) ||
      !publicSupportIntentPattern.test(prompt))
  ) {
    return "**Account details:** log in to the Traderflow dashboard for your plan, billing, trades, certificates, payouts, or private profile data."
  }

  if (
    tradingAdvicePattern.test(prompt) ||
    (!helpPattern.test(prompt) && marketOnlyPattern.test(prompt))
  ) {
    return "**Limited here:** website help only covers Traderflow FAQs, competitions, pricing, login, and setup. Log in with Pro or higher for Forex Copilot analysis."
  }

  if (
    !helpPattern.test(prompt) &&
    !publicSupportIntentPattern.test(prompt) &&
    !liveCompetitionPattern.test(prompt)
  ) {
    return "**Support topics only:** FAQs, competitions, pricing, login/register, account setup, TraderPass, certificates, verified trades, and Discord/Telegram sharing."
  }

  return ""
}

function consumePublicHelpRequest() {
  if (typeof window === "undefined") return true

  try {
    const now = Date.now()
    const parsed = JSON.parse(
      window.localStorage.getItem(publicRateLimit.key) || "{}"
    )
    const startedAt =
      Number.isFinite(Number(parsed.startedAt)) &&
      now - Number(parsed.startedAt) < publicRateLimit.windowMs
        ? Number(parsed.startedAt)
        : now
    const count = startedAt === now ? 0 : Number(parsed.count) || 0

    if (count >= publicRateLimit.maxRequests) {
      return false
    }

    window.localStorage.setItem(
      publicRateLimit.key,
      JSON.stringify({ startedAt, count: count + 1 })
    )
    return true
  } catch {
    return true
  }
}

async function fetchLiveCompetitionReply() {
  if (!awsExports.preview_domain) {
    return "**Live competitions:** open the Competitions page to see current events and leaderboards."
  }

  try {
    const response = await fetch(
      `https://${awsExports.preview_domain}/competitions?status=LIVE`,
      {
        headers: {
          Accept: "application/json",
        },
      }
    )

    if (!response.ok) {
      throw new Error(`Competition API returned ${response.status}`)
    }

    const payload = await response.json()
    const competitions = Array.isArray(payload.items) ? payload.items : []
    if (!competitions.length) {
      return "**Live competitions:** none are live right now. Check the Competitions page again soon."
    }

    const rows = competitions.slice(0, 5).map((competition, index) => {
      const endDate = competition.endDate
        ? formatShortDate(competition.endDate)
        : "end date TBC"
      const joined = Number.isFinite(Number(competition.tradersJoined))
        ? `${competition.tradersJoined} traders`
        : "trader count TBC"
      return `${index + 1}. ${
        competition.name || "Traderflow competition"
      } - ${joined}, ends ${endDate}.`
    })

    return [
      "**Live competitions:**",
      ...rows,
      "",
      "Open the Competitions page to join or view the leaderboard.",
    ].join("\n")
  } catch {
    return "**Live competitions:** I could not load them right now. Open the Competitions page to check current events."
  }
}

function buildLimitedReply(prompt) {
  const symbol = inferSymbol(prompt)
  const timeframe = inferTimeframe(prompt)

  if (helpPattern.test(prompt) || publicSupportIntentPattern.test(prompt)) {
    return buildTraderflowHelpReply(prompt)
  }

  if (!marketOnlyPattern.test(prompt)) {
    return "**Support topics only:** ask about FAQs, competitions, pricing, account setup, TraderPass, Discord/Telegram, certificates, verified trades, or registration."
  }

  return `**Limited ${symbol} ${timeframe}:** I can discuss public pricing context, but full trade recommendations require dashboard login with Pro or higher.`
}

function buildTraderflowHelpReply(prompt) {
  const lower = prompt.toLowerCase()

  if (/\b(faqs?|frequently asked|questions?)\b/.test(lower)) {
    return [
      "**Traderflow FAQ topics:**",
      "1. Competitions, rules, leaderboard, rank, and scoring.",
      "2. Pricing, plans, Pro access, and Partner workflows.",
      "3. MT4/MT5 account connection with read-only investor access.",
      "4. TraderPass, verified trades, certificates, and payout proof.",
      "5. Discord/Telegram sharing and login/register help.",
      "",
      "Ask one topic for a short answer.",
    ].join("\n")
  }
  if (lower.includes("competition") || /\bcomp\b/.test(lower)) {
    return "**Competitions:** join events, track leaderboard rank, and compete using verified trading performance. Ask about joining, scoring, rules, or a specific competition."
  }
  if (
    lower.includes("pricing") ||
    lower.includes("plan") ||
    lower.includes("membership")
  ) {
    return "**Pricing:** plans cover platform access, Premium features, Pro Copilot, and Partner workflows. Pro or higher unlocks full Copilot trading analysis."
  }
  if (
    lower.includes("account") ||
    lower.includes("mt4") ||
    lower.includes("mt5") ||
    lower.includes("broker")
  ) {
    return "**Account setup:** log in, add your MT4/MT5 broker and server, then use a read-only investor password so Traderflow can verify trades."
  }
  if (lower.includes("traderpass") || lower.includes("trader pass")) {
    return "**TraderPass:** your verified profile built from connected account activity, score, history, and achievements."
  }
  if (
    lower.includes("performance") ||
    lower.includes("improve") ||
    lower.includes("best trade")
  ) {
    return "**Trade performance:** review your journal, win rate, profit factor, drawdown, and best/worst setups in Traderflow. For deeper account analysis, log in and use Pro Copilot with a connected account."
  }
  if (
    lower.includes("certificate") ||
    lower.includes("payout") ||
    lower.includes("verify")
  ) {
    return "**Certificates:** Traderflow verifies certificates, payouts, and trade records for stronger proof. Use dashboard certificate tools after login."
  }
  if (lower.includes("discord") || lower.includes("telegram")) {
    return "**Discord/Telegram:** connect a destination in the dashboard, then choose which verified trades or updates should publish."
  }
  if (lower.includes("register") || lower.includes("login")) {
    return "**Login/Register:** create an account with Register, then log in to connect accounts, join competitions, manage membership, and use eligible Copilot tools."
  }

  return "**Traderflow Help:** choose competitions, pricing, login/register, account setup, TraderPass, certificates, verified trades, or Discord/Telegram."
}

function renderFormattedText(text) {
  return String(text)
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={index}>{part.slice(2, -2)}</strong>
      }
      return <React.Fragment key={index}>{part}</React.Fragment>
    })
}

function inferSymbol(prompt) {
  const compact = prompt.toUpperCase().replace(/[^A-Z]/g, "")
  return knownSymbols.find(symbol => compact.includes(symbol)) || "EURUSD"
}

function inferTimeframe(prompt) {
  const normalized = prompt.toLowerCase()
  if (/\b(1h|hour|intraday)\b/.test(normalized)) return "1H"
  if (/\b(4h|session)\b/.test(normalized)) return "4H"
  if (/\b(day|daily|1d)\b/.test(normalized)) return "1D"
  if (/\b(week|weekly|1w)\b/.test(normalized)) return "1W"
  return "1W"
}

function formatShortDate(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "end date TBC"
  return date.toLocaleDateString("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

function getDashboardUrl() {
  return getAppBaseUrl()
}

function getHelpApiUrls() {
  const configured = process.env.GATSBY_FOREX_COPILOT_HELP_API_URL
  const fromAwsExports = awsExports.forex_copilot_api_url
  const candidates = [configured, fromAwsExports]

  if (typeof window !== "undefined") {
    candidates.push(`${window.location.origin}/forex-copilot/help/respond`)
  }

  if (awsExports.preview_domain) {
    candidates.push(
      `https://${awsExports.preview_domain}/forex-copilot/help/respond`
    )
  }

  return [
    ...new Set(
      candidates.filter(Boolean).map(baseUrl => {
        const normalized = String(baseUrl).replace(/\/$/, "")
        if (normalized.endsWith("/forex-copilot/help/respond")) {
          return normalized
        }
        return `${normalized}/forex-copilot/help/respond`
      })
    ),
  ]
}

function createMessageId() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID()
  }
  return `message-${Date.now()}-${Math.random().toString(36).slice(2)}`
}
